import { BasePick, Sport } from '../domain/types';

type EspnTeam = { id?: string; abbreviation?: string; name?: string; displayName?: string; shortDisplayName?: string; location?: string; logos?: { href?: string }[] };
type TeamRow = { team?: EspnTeam };
type RosterItem = { id?: string; fullName?: string; displayName?: string; items?: RosterItem[] };
const ESPN = 'https://site.api.espn.com/apis/site/v2/sports';
const tidy = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ').trim().replace(/\s+(jr|sr|ii|iii|iv|v)$/i, '');
const sportPath = (sport: Sport) => sport === Sport.NBA ? 'basketball/nba' : 'hockey/nhl';
const cacheKey = (sport: Sport, player: string) => `rushprops.teamLogo.v2.${sport}.${tidy(player)}`;
const memory = new Map<string, string>();
const teamsCache = new Map<Sport, Promise<TeamRow[]>>();
const rostersCache = new Map<string, Promise<RosterItem[]>>();

async function teams(sport: Sport): Promise<TeamRow[]> {
  let pending = teamsCache.get(sport);
  if (!pending) {
    pending = fetch(`${ESPN}/${sportPath(sport)}/teams?limit=100`).then(r => r.ok ? r.json() : Promise.reject()).then(data => data.sports?.[0]?.leagues?.[0]?.teams ?? []);
    teamsCache.set(sport, pending);
  }
  return pending;
}
async function roster(sport: Sport, id: string): Promise<RosterItem[]> {
  const key = `${sport}:${id}`;
  let pending = rostersCache.get(key);
  if (!pending) {
    pending = fetch(`${ESPN}/${sportPath(sport)}/teams/${id}/roster`).then(r => r.ok ? r.json() : Promise.reject()).then(data => (data.athletes ?? []).flatMap((row: RosterItem) => row.items ?? [row]));
    rostersCache.set(key, pending);
  }
  return pending;
}
function aliases(team: EspnTeam) {
  return [team.abbreviation, team.shortDisplayName, team.name, team.displayName, team.location]
    .filter((value): value is string => Boolean(value)).map(tidy).filter(Boolean);
}
function matchupTeams(game: string, rows: TeamRow[]): EspnTeam[] {
  const sides = game.split(/\s+@\s+/).map(tidy);
  if (sides.length !== 2) return [];
  return sides.map(side => rows.map(row => row.team).filter((team): team is EspnTeam => Boolean(team?.id && team.logos?.[0]?.href))
    .find(team => aliases(team).some(alias => alias === side || side === alias.split(' ').slice(-1)[0] || side.endsWith(` ${alias}`) || alias.endsWith(` ${side}`)))
  ).filter((team): team is EspnTeam => Boolean(team));
}

export async function loadPlayerPhotos(picks: BasePick[]): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  await Promise.all([Sport.NBA, Sport.NHL].map(async sport => {
    const players = picks.filter(p => p.sport === sport);
    const missing = players.filter(p => {
      const key = cacheKey(sport, p.player);
      const cached = memory.get(key) ?? (() => { try { return localStorage.getItem(key) ?? undefined; } catch { return undefined; } })();
      if (cached) result.set(p.id, cached);
      return !cached;
    });
    if (!missing.length) return;
    try {
      const rows = await teams(sport);
      const gameRows = new Map<string, EspnTeam[]>();
      for (const pick of missing) gameRows.set(pick.game, matchupTeams(pick.game, rows));
      const teamIds = new Set(Array.from(gameRows.values()).flat().map(team => team.id!).filter(Boolean));
      const rosters = new Map<string, RosterItem[]>();
      await Promise.all(Array.from(teamIds).map(async id => {
        try { rosters.set(id, await roster(sport, id)); } catch { rosters.set(id, []); }
      }));
      for (const pick of missing) {
        const playersTeams = (gameRows.get(pick.game) ?? []).filter(team => {
          const athletes = rosters.get(team.id!) ?? [];
          const wanted = tidy(pick.player);
          return athletes.some(a => tidy(a.fullName ?? a.displayName ?? '') === wanted);
        });
        // If a player's ESPN spelling differs, only accept a unique surname + first initial match.
        const exact = playersTeams[0];
        const gameTeamRows = gameRows.get(pick.game) ?? [];
        let found = exact;
        if (!found) {
          const wanted = tidy(pick.player).split(' ');
          const candidates = gameTeamRows.filter(team => (rosters.get(team.id!) ?? []).some(a => {
            const parts = tidy(a.fullName ?? a.displayName ?? '').split(' ');
            return parts[parts.length - 1] === wanted[wanted.length - 1] && parts[0]?.[0] === wanted[0]?.[0];
          }));
          if (candidates.length === 1) found = candidates[0];
        }
        const logo = found?.logos?.[0]?.href;
        if (!logo) continue;
        const key = cacheKey(sport, pick.player);
        memory.set(key, logo);
        try { localStorage.setItem(key, logo); } catch { /* Cache remains in memory if storage is unavailable. */ }
        result.set(pick.id, logo);
      }
    } catch { /* Show the team abbreviation fallback when ESPN cannot be reached. */ }
  }));
  return result;
}
